import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Languages } from 'lucide-react';

/**
 * Language-suggestion popup — shown once per session.
 * When the current language is German, it informs English-speaking visitors
 * (and vice versa). Non-blocking, bottom-right corner, auto-hides after ~8s.
 */
const LanguagePrompt = () => {
  const { language, setLanguage, t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = sessionStorage.getItem('lang_prompt_dismissed');
    if (dismissed) return;

    // delay appearance so it doesn't compete with first-paint
    const showTimer = setTimeout(() => {
      setVisible(true);
      // trigger CSS transition on next tick
      setTimeout(() => setEntered(true), 20);
    }, 1800);

    // auto-hide
    const hideTimer = setTimeout(() => {
      setEntered(false);
      setTimeout(() => setVisible(false), 350);
    }, 10000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const dismiss = () => {
    setEntered(false);
    setTimeout(() => setVisible(false), 300);
    try {
      sessionStorage.setItem('lang_prompt_dismissed', '1');
    } catch (e) {
      /* ignore */
    }
  };

  const switchLang = () => {
    setLanguage(language === 'de' ? 'en' : 'de');
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      className={`fixed z-50 bottom-5 right-5 max-w-[320px] transition-all duration-300 ${
        entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <div className="bg-[#1A2231] border border-white/10 rounded-lg shadow-lg p-4 pr-9 relative">
        <button
          onClick={dismiss}
          aria-label={t('common.close')}
          className="absolute top-2.5 right-2.5 p-1 rounded text-slate-400 hover:text-slate-300 hover:bg-white/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-md bg-[#4A8FD1]/10 flex items-center justify-center">
            <Languages className="w-4 h-4 text-[#4A8FD1]" />
          </div>
          <div className="flex-1 pt-0.5">
            <p className="text-sm text-slate-300 leading-relaxed">
              {t('common.langPrompt')}
            </p>
            <button
              onClick={switchLang}
              className="mt-2 text-xs font-medium text-[#4A8FD1] hover:underline mono"
            >
              {t('common.langPromptCTA')} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguagePrompt;
